/**
 * Uberth Hernandez: pardot-iframe.esm.js (ES6)
 * Módulo para manejar formularios de Pardot vía iframe con postMessage
 */

import { PREFIX } from '../helpers/config.js';
import { Toasts } from '../helpers/toasts.js';

const SELECTOR_DATA = `[data-${PREFIX ?? 'wphack'}-pardot-iframe]`;

const DEFAULTS = {
  formId: 'pardot-iframe-form',
  formName: 'Pardot Iframe Form',
  eventName: 'Form Submit',
  successMessage: '¡Formulario enviado correctamente! Nos pondremos en contacto contigo pronto.',
  errorMessage: 'Error al enviar el formulario. Por favor, inténtalo de nuevo.',
};

export class PardotIframe {
  /**
   * @param {HTMLElement} container Contenedor con data-pardot-iframe
   * @param {Object} config Configuración opcional
   */
  constructor(container, config = {}) {
    if (!(container instanceof HTMLElement)) {
      throw new Error('PardotIframe: container inválido');
    }

    this._container = container;

    const datasetConfig = this._parseDatasetConfig(container);
    this._config = {
      ...DEFAULTS,
      ...config,
      ...datasetConfig
    };

    console.log( '%c=> pardot-iframe.esm.js => Activated', 'background-color:green;color:#fff;padding: 5px 10px;' );
    console.table( this._config );

    // Verificar dependencias críticas
    this._checkDependencies();

    this._handleMessage = this._handleMessage.bind(this);

    this._init();
  }

  /**
   * Verifica que las dependencias críticas estén disponibles
   */
  _checkDependencies() {
    const dependencies = {
      'Toasts helper': Toasts,
      'Bootstrap (window.bootstrap)': typeof window !== 'undefined' && window.bootstrap
    };

    Object.entries(dependencies).forEach(([name, dependency]) => {
      if (!dependency) {
        console.warn(`PardotIframe: Missing dependency: ${name}`);
      } else {
        console.log(`PardotIframe: ✓ ${name} is available`);
      }
    });
  }

  _parseDatasetConfig(container) {
    const dataAttr = container.dataset[`${PREFIX}PardotIframe`] || 
                    container.dataset.wphackPardotIframe || 
                    container.dataset.pardotIframe;
    
    if (!dataAttr) return {};

    try {
      return JSON.parse(dataAttr);
    } catch (error) {
      console.warn('PardotIframe: Error parsing config from dataset:', error);
      return {};
    }
  }

  /**
   * Inicializa el módulo
   */
  _init() {
    window.addEventListener('message', this._handleMessage, false);

    // Inicializar dataLayer si no existe
    if (typeof window.dataLayer === 'undefined') {
      window.dataLayer = [];
    }

    // Log de inicialización
    console.log('PardotIframe: Initialized for container:', this._container);
  }

  _handleMessage(event) {
    // Filtrar mensajes de localhost no autorizados (evita spam en consola)
    if (event.origin && event.origin.includes('localhost:8080')) {
      return;
    }

    // Verificar que es un mensaje válido de Pardot
    if (!event.data || event.data.type !== 'wam-gtm') {
      return;
    }

    console.log('PardotIframe: Received postMessage:', event.data);

    // Determinar si es éxito o error
    const status = event.data.status || 'unknown';
    
    if (status === 'success') {
      this._handleSuccess(event.data);
    } else if (status === 'error') {
      this._handleError(event.data);
    } else {
      this._handleSuccess(event.data);
    }
  }

  _handleSuccess(data) {
    console.log('%cPardotIframe => Success', 'color:green;', data);

    try {
      // Verificar que Toasts esté disponible
      if (!Toasts) {
        console.error('PardotIframe: Toasts helper not available');
        return;
      }

      // Mostrar toast de éxito usando el sistema existente
      const successHtml = this._generateToastHTML(this._config.successMessage, 'success');
      console.log('PardotIframe: Generated toast HTML:', successHtml);
      
      Toasts.show(successHtml);
      console.log('PardotIframe: Toast.show() called successfully');
      
    } catch (error) {
      console.error('PardotIframe: Error showing success toast:', error);
    }

    // Enviar evento al dataLayer
    this._sendToDataLayer({
      event: data.event || this._config.eventName,
      formId: this._config.formId,
      formName: this._config.formName,
      formLanguage: data.eventLabel || 'ES',
      status: 'success'
    });
  }

  _handleError(data) {
    console.error('%cPardotIframe => Error', 'color:red;', data);

    try {
      // Verificar que Toasts esté disponible
      if (!Toasts) {
        console.error('PardotIframe: Toasts helper not available for error');
        return;
      }

      // Mostrar toast de error usando el sistema existente
      const errorHtml = this._generateToastHTML(this._config.errorMessage, 'danger');
      console.log('PardotIframe: Generated error toast HTML:', errorHtml);
      
      Toasts.show(errorHtml);
      console.log('PardotIframe: Error toast.show() called successfully');
      
    } catch (error) {
      console.error('PardotIframe: Error showing error toast:', error);
    }

    // Enviar evento de error al dataLayer
    this._sendToDataLayer({
      event: 'Form Error',
      formId: this._config.formId,
      formName: this._config.formName,
      status: 'error',
      errorMessage: data.errorMessage || 'Unknown error'
    });
  }

  _generateToastHTML(message, type = 'success') {
    return `
      <div class="toast text-bg-${type}" role="alert" aria-live="assertive" aria-atomic="true" data-bs-autohide="true" data-bs-delay="4000">
        <div class="toast-body">${message}</div>
        <button type="button" class="btn btn-close btn-sm p-1" data-bs-dismiss="toast" aria-label="Close">
          <svg class="bc bi bi-square-sm">
            <use xlink:href="#custom-close"></use>
          </svg>
        </button>
      </div>
    `;
  }

  _sendToDataLayer(eventData) {
    const layer = {
      event: eventData.event,
      formId: eventData.formId,
      formName: eventData.formName,
      formLanguage: eventData.formLanguage || 'ES',
      status: eventData.status,
      ...eventData
    };

    window.dataLayer.push(layer);
    console.log('PardotIframe => DataLayer event sent:', layer);
  }

  destroy() {
    window.removeEventListener('message', this._handleMessage, false);
    this._container = null;
    console.log('PardotIframe: Instance destroyed');
  }

  static initAll(root = document) {
    const containers = root.querySelectorAll(SELECTOR_DATA);
    const instances = [];
    
    containers.forEach((container) => {
      if (container._pardotIframeInstance) {
        return;
      }
      
      const instance = new PardotIframe(container);
      container._pardotIframeInstance = instance;
      instances.push(instance);
    });

    console.log(`PardotIframe: Initialized ${instances.length} instances`);
    return instances;
  }

  static destroyAll(root = document) {
    const containers = root.querySelectorAll(SELECTOR_DATA);
    
    containers.forEach((container) => {
      if (container._pardotIframeInstance) {
        container._pardotIframeInstance.destroy();
        container._pardotIframeInstance = null;
      }
    });
  }
}

export default PardotIframe;